import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {Injectable, inject, signal, OnDestroy} from '@angular/core';
import { BehaviorSubject, Observable, map, switchMap, tap, take } from 'rxjs';
import {User} from '../core/classes/user';
import {Router} from '@angular/router';
import { enrichUserWithPreferences } from './user-preferences.helper';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {

  private static authenticatedUserSubject = new BehaviorSubject<User | undefined>(undefined);
  static authenticatedUser = AuthService.authenticatedUserSubject.asObservable();

  static getAuthenticatedUserValue(): User | undefined {
    return AuthService.authenticatedUserSubject.getValue();
  }

  private readonly apiUrl = 'api/security/user';
  private http = inject(HttpClient);
  private router = inject(Router);

  private sessionTimeoutHandle: ReturnType<typeof setInterval> | undefined;

  ngOnDestroy() {
    this.stopSessionPolling();
  }

  public login(username: string, password: string): Observable<User> {
    const url = '/login';
    const params = new HttpParams()
      .set('username', username)
      .set('password', password);

    return this.http.post(url, params, {responseType: 'text' as const})
      .pipe(
        switchMap(() => {
          return this.getAuthenticatedUser();
        }),
      );
  }

  public logout(): Observable<string> {
    const url = '/logout';
    return this.http.post(url, null, {responseType: 'text' as const}).pipe(
      tap(() => {
        AuthService.authenticatedUserSubject.next(undefined);
      }),
    );
  }

  public getAuthenticatedUser(): Observable<User> {
    const url = `${this.apiUrl}/me`;
    return this.http.get<User>(url).pipe(
      map(user => new User(user)),
      switchMap(user => enrichUserWithPreferences(this.http, user, {
        logPrefix: 'There was an error fetching user preferences',
      })),
      tap(user => {
        AuthService.authenticatedUserSubject.next(user);
      }),
    );
  }

  public isLoggedIn(): Observable<boolean> {
    return this.getAuthenticatedUser().pipe(map(user => {
      if (!user?.id) {
        // noinspection JSIgnoredPromiseFromCall
        this.router.navigate(['/login']);
        return false;
      }
      return true;
    }));
  }

  /**
   * Checks for appropriate password complexity
   *
   * @param password
   */
  public checkPasswordComplexity(password: string): Observable<string> {
    const url = `${this.apiUrl}/check-password-complexity`;
    return this.http.post(url, password, {
      responseType: 'text',
    });
  }

  public verifyPassword(password: string): Observable<boolean> {
    const url = `${this.apiUrl}/verify-password`;
    return this.http.post<boolean>(url, password);
  }

  public requestAccount(user: User): Observable<User> {
    const url = `${this.apiUrl}/request-account`;
    return this.http.post<User>(url, user);
  }

  public requestPasswordReset(email: string): Observable<boolean> {
    const url: string = `api/security/user/request-password-reset?email=${email}`;
    return this.http.post<boolean>(url, '');
  }

  public changePassword(userId: number, password: string): Observable<void> {
    return this.http.post<void>(`api/security/user/changeuserpassword/${userId}`, password);
  }

  public resetPasswordFromCode(password: string): Observable<void> {
    return this.http.post<void>('api/security/user/change-password', password);
  }


  public impersonateUser(user: User): Observable<User> {
    const url = '/admin/impersonate';
    const headers = new HttpHeaders()
      .set('Content-Type', 'application/x-www-form-urlencoded');

    return this.http.post(url, `username=${user.username}`, {headers}).pipe(
      switchMap(() => this.getAuthenticatedUser()),
    );
  }

  public unimpersonate(): Observable<User> {
    const url = '/admin/unimpersonate';
    return this.http.post(url, '').pipe(
      switchMap(() => this.getAuthenticatedUser()),
    );
  }


  public getExistingUsers(): Observable<User[]> {
    const url = 'api/security/user';
    return this.http.get<User[]>(url).pipe(
      map(res => res.map(user => new User(user))),
    );
  }

  public isSessionConnected = signal(true);

  public startSessionPolling(retryInMs: number = 5 * 60 * 1000): void {
    if (this.sessionTimeoutHandle) {
      return;
    }

    const url = `${this.apiUrl}/session`;
    const checkSession = () => {
      this.http.get(url).pipe(
        take(1),
      ).subscribe({
        next: () => this.isSessionConnected.set(true),
        error: () => this.isSessionConnected.set(false),
      });
    };

    // Perform an immediate connectivity check before entering the polling cadence.
    checkSession();
    this.sessionTimeoutHandle = setInterval(checkSession, retryInMs);
  }

  public stopSessionPolling(): void {
    if (!this.sessionTimeoutHandle) {
      return;
    }

    clearInterval(this.sessionTimeoutHandle);
    this.sessionTimeoutHandle = undefined;
  }

  checkEmailExists(email: string): Observable<boolean> {
    return this.http.post<boolean>('api/security/user/check-email-exists', email);
  }

  checkUsernameExists(username: string): Observable<string> {
    return this.http.post('api/security/user/check-username-exists', username, {responseType: 'text'});
  }

}
